import { useMutation } from '@apollo/react-hooks'
import * as Query from '../../../api/gql/query'
import * as Mutation from '../../../api/gql/mutation'
import { historyVariables } from '../lib/records'
import type { CostExpense } from '../types'
import { WRITE, useCostWriteQueue, type Mutate, type Reload, type Vars } from './useCostWriteQueue'

/**
 * Expense and expense-attachment writes (spec §6). Expenses live in the ledger (ruling Q9), so every write also
 * reloads `listReloads` — the ledger's current queries — as well as the unfiltered expense list.
 */
export function useExpenseMutations(projectId: string, listReloads: Reload[], onDetailError?: (expenseId: string, error: unknown) => void) {
  const { saving, run } = useCostWriteQueue()
  const [create] = useMutation(Mutation.CREATE_COST_EXPENSE, WRITE)
  const [update] = useMutation(Mutation.UPDATE_COST_EXPENSE, WRITE)
  const [remove] = useMutation(Mutation.DELETE_COST_EXPENSE, WRITE)
  const [addAttachment] = useMutation(Mutation.ADD_COST_ATTACHMENT, WRITE)
  const [removeAttachment] = useMutation(Mutation.REMOVE_COST_ATTACHMENT, WRITE)
  const lists: Reload[] = [{ query: Query.COST_EXPENSES, variables: { projectId, filter: {} } }, ...listReloads]
  const detail = (id: string): Reload[] => [
    { query: Query.COST_EXPENSE, variables: { id }, onError: onDetailError && ((e: unknown) => onDetailError(id, e)) },
    { query: Query.COST_ACTIVITY_LOG, variables: historyVariables(projectId, 'EXPENSE', id) },
  ]
  const m = (fn: unknown) => fn as Mutate

  return {
    saving,
    create: (input: Vars) => run<{ createCostExpense: CostExpense }>(m(create), { projectId, input }, lists),
    update: (id: string, input: Vars) => run(m(update), { id, input }, [...detail(id), ...lists]),
    remove: (id: string) => run(m(remove), { id }, lists, detail(id)),
    addAttachment: (id: string, source: { fileId: string } | { docRevisionId: string }) => run(m(addAttachment), { target: { expenseId: id }, ...source }, detail(id)),
    removeAttachment: (id: string, attachmentId: string) => run(m(removeAttachment), { id: attachmentId }, detail(id)),
  }
}

export type ExpenseMutations = ReturnType<typeof useExpenseMutations>
